'use client';

import * as React from 'react';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '../lib/utils';

const TooltipProvider: React.FC<
  React.ComponentPropsWithRef<typeof TooltipPrimitive.Provider>
> = ({ delayDuration = 0, ...props }) => (
  <TooltipPrimitive.Provider delayDuration={delayDuration} {...props} />
);

const Tooltip = TooltipPrimitive.Root;

/** Marks a trigger whose click keeps its tooltip open; the content reads it. */
const KEEP_OPEN_ATTRIBUTE = 'data-keep-open-on-click';

/**
 * Radix closes a tooltip when its trigger is clicked, made for a trigger that
 * does something. `keepOpenOnClick` is for one whose click does nothing, such
 * as a help icon or a disabled control: clicking it does not hide what it
 * explains. Radix closes twice over, and both are marked as handled, which is
 * what keeps it from closing: the pointer down, which the content takes as
 * outside itself (see `TooltipContent`), and the click. So never use it on a
 * link, a submit button or a label.
 */
const TooltipTrigger: React.FC<
  React.ComponentPropsWithRef<typeof TooltipPrimitive.Trigger> & {
    keepOpenOnClick?: boolean;
  }
> = ({ keepOpenOnClick = false, onClick, ...props }) => (
  <TooltipPrimitive.Trigger
    {...(keepOpenOnClick && { [KEEP_OPEN_ATTRIBUTE]: '' })}
    onClick={
      keepOpenOnClick
        ? (event) => {
            onClick?.(event);
            event.preventDefault();
          }
        : onClick
    }
    {...props}
  />
);
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipContent: React.FC<
  React.ComponentPropsWithRef<typeof TooltipPrimitive.Content>
> = ({ className, sideOffset = 12, onPointerDownOutside, ...props }) => (
  // Portal + z-[3000]: tooltips must escape scrollable dialog content
  // (overflow-y-auto) and render above DialogContent (z-[2000]).
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      sideOffset={sideOffset}
      // A pointer down on a trigger that keeps its tooltip open is not a
      // reason to close it, see `TooltipTrigger`.
      onPointerDownOutside={(event) => {
        onPointerDownOutside?.(event);
        const target = event.target as Element | null;
        if (target?.closest?.(`[${KEEP_OPEN_ATTRIBUTE}]`)) {
          event.preventDefault();
        }
      }}
      className={cn(
        'z-[3000] overflow-hidden rounded-lg bg-black px-2.5 py-3 text-xs text-white animate-in fade-in-0 zoom-in-95 [text-box-edge:cap_alphabetic] [text-box-trim:trim-both] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
